/**
 * THE OPERATIONAL DOCUMENTS, READ FROM THE REPOSITORY AT BUILD TIME.
 *
 * ONE SOURCE OF TRUTH, AND THIS IS THE WHOLE OF HOW.
 *
 * `?raw` inlines the file's bytes into the bundle. There is no copy here to
 * fall behind: editing `docs/PILOT-GO-LIVE.md` changes the console at the next
 * build, and there is no second place a developer has to remember. A page that
 * paraphrased these files would be wrong within a month, and wrong runbooks
 * are worse than none because somebody follows them during an incident.
 *
 * WHAT IS NOT HERE, AND WHY.
 *
 * `RUNBOOK.md` at the repository root is a DEVELOPMENT runbook: installing
 * dependencies, starting emulators, seeding local data. It is for somebody
 * with the repository checked out, not for somebody operating the service, and
 * it still names a local path from a machine that no longer holds the code. It
 * does not belong in an operations console and is deliberately excluded.
 *
 * Everything else under `docs/` is architecture, data model, specifications
 * and history. Adding one because it is a document would make this a
 * documentation browser, which is not what an operator needs at 3am.
 */

import goLive from '../../../../docs/PILOT-GO-LIVE.md?raw';
import disasterRecovery from '../../../../docs/PILOT-DISASTER-RECOVERY.md?raw';
import platform from '../../../../docs/PLATFORM-OPERATIONS.md?raw';

export interface RunbookDoc {
  /** The last path segment, and the anchor for a link somebody pastes. */
  id: string;
  /** What the sub-navigation calls it. */
  label: string;
  /** One line, so a reader knows which document they want before opening it. */
  blurb: string;
  source: string;
}

export const RUNBOOK_DOCS: RunbookDoc[] = [
  {
    id: 'runbook',
    label: 'Go-live',
    blurb: 'Putting the first paying customer on StandingDocs, and running them for a month.',
    source: goLive,
  },
  {
    id: 'disaster-recovery',
    label: 'Disaster recovery',
    blurb: 'What protects the production database, how to find a recovery point, and how to use one.',
    source: disasterRecovery,
  },
  {
    /**
     * Where the twelve archived `audience: 'sysadmin'` help articles went.
     * They were platform documentation living in the customer application's
     * help centre, readable by any signed-in customer because nothing
     * enforced the audience field, and wrong in every case a September 2026
     * audit checked. Reference rather than procedure, which is why it sits
     * after the two runbooks.
     */
    id: 'platform',
    label: 'Platform reference',
    blurb: 'What the platform is made of, which settings are really enforced, and what has no console page.',
    source: platform,
  },
];

export const DEFAULT_DOC_ID = 'runbook';

export function docById(id: string | undefined): RunbookDoc | undefined {
  return RUNBOOK_DOCS.find((d) => d.id === id);
}
